import type {ReactNode} from "react";

import {EmptyState} from "@project/design-system-web";
import {useI18n} from "@project/i18n/react";
import {EmptyStateIcon} from "@project/icons-web";

import {componentMessages} from "../../components.messages";

interface TableEmptyStateProps {
  /** Custom message shown below the icon (default: "No results found") */
  message?: string;
  /** Custom icon element; defaults to the generic `EmptyStateIcon` */
  icon?: ReactNode;
}

/**
 * TableEmptyState — standard empty-state panel for data tables.
 *
 * Rendered via `Table.Body`'s `renderEmptyState` prop. Provides a consistent
 * empty-state experience across all entity tables with an icon and message.
 * Both are overridable for domain-specific copy without code duplication.
 *
 * @example
 * ```tsx
 * <Table.Body renderEmptyState={() => <TableEmptyState />}>
 *   ...
 * </Table.Body>
 *
 * // With custom message:
 * <Table.Body renderEmptyState={() => <TableEmptyState message="No users found" />}>
 *   ...
 * </Table.Body>
 * ```
 */
export function TableEmptyState({icon, message}: TableEmptyStateProps) {
  const {t} = useI18n();
  const resolvedMessage = message ?? t(componentMessages.tableEmptyStateNoResults);

  return (
    <EmptyState className="flex h-full w-full flex-col items-center justify-center gap-4 text-center">
      {icon ?? <EmptyStateIcon className="text-muted size-6" />}
      <span className="text-muted text-sm">{resolvedMessage}</span>
    </EmptyState>
  );
}
